import { Component } from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { CommonModule } from '@angular/common';
import { CompanyService } from '../../shared/services/company.service';
import { DashboardMetricsService } from '../../shared/services/dashboard-metrics.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderComponent } from '../../components/loader/loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../shared/services/language.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { Company } from '../../models/company.model';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-home',
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  isSidenavVisible: boolean = false;
  activeQuotes: number = 0;

  userData: any;
  isAuthenticated!: boolean;
  userFullName!: string;
  isLoading: boolean = false;

  // Dashboard metrics
  companyCount: number = 0;
  totalEmployees: number = 0;
  totalActivePolicies: number = 0;
  expiredPolicies: number = 0;

  userId: any;
  revenueChange: any;
  claimsChange: any;
  totalReports: any;
  currentLang: string = 'en';
  private destroy$ = new Subject<void>();
  currentDirection: 'ltr' | 'rtl' = 'ltr';

  companies: Company[] = [];
  currentSelectedCompanyId: number = 0;
  userInitials: any;

  constructor(
    private authService: AuthService,
    private companyService: CompanyService,
    private dashboardMetricsService: DashboardMetricsService,
    private toastr: ToastrService,
    public languageService: LanguageService
  ) {}
  toggleSidenav(): void {
    this.isSidenavVisible = !this.isSidenavVisible;
  }

  private setUserAvatar(): void {
    if (this.userFullName && this.userFullName !== 'User') {
      this.userInitials = this.userFullName
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
  }

  ngOnInit() {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      console.log('Authentication status:', isAuthenticated);
      this.isAuthenticated = isAuthenticated;
      this.languageService.currentLang$
        .pipe(takeUntil(this.destroy$))
        .subscribe((lang) => {
          this.currentLang = lang;
        });

      // Subscribe to direction changes
      this.languageService.direction$
        .pipe(takeUntil(this.destroy$))
        .subscribe((direction) => {
          this.currentDirection = direction;
        });

      this.authService
        .getUserInfo()
        .then((profile) => {
          this.userData = profile;
          localStorage.setItem('keycloakUserProfile', JSON.stringify(profile));
          console.log('User profile:', profile);

          this.userFullName =
            profile?.firstName + ' ' + profile?.lastName || 'User';
          this.setUserAvatar();
        })
        .catch((error) => {
          console.error('Error fetching user profile:', error);
          this.userFullName = 'User'; // Fallback in case of error
        });
    });

    this.userId = this.getUserIdFromLocalStorage();
    if (this.userId) {
      this.fetchCompanies();
      this.companyService.currentSelectedCompany$.subscribe((companyId) => {
        this.currentSelectedCompanyId = companyId;
        this.loadDashboardMetrics();
      });
    }
  }

  private getUserIdFromLocalStorage(): string {
    const keycloakProfile = localStorage.getItem('keycloakUserProfile');
    if (keycloakProfile) {
      try {
        const profile = JSON.parse(keycloakProfile);
        return profile.id;
      } catch (error) {
        console.error('Error parsing Keycloak profile:', error);
        return '';
      }
    }
    return '';
  }

  /**
   * Load all dashboard metrics using the new service
   */
  loadDashboardMetrics(): void {
    if (!this.userId) {
      console.warn('No user ID available for loading dashboard metrics');
      return;
    }

    this.isLoading = true;

    // Use the combined metrics method for better performance
    this.dashboardMetricsService
      .getAllMetricsForUser(this.userId, this.currentSelectedCompanyId)
      .subscribe({
        next: (metrics) => {
          this.totalEmployees = metrics.totalEmployees;
          this.companyCount = metrics.totalCompanies;
          this.totalActivePolicies = metrics.totalActivePolicies;
          this.activeQuotes = metrics.activeQuotes;
          this.isLoading = false;
          console.log('Dashboard metrics loaded:', metrics);
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Error loading dashboard metrics:', err);
          this.toastr.error('Failed to load dashboard metrics', 'Error');

          // Fallback: try to load metrics individually
          this.loadMetricsIndividually();
        },
      });
  }

  /**
   * Fallback method to load metrics individually if the combined method fails
   */
  private loadMetricsIndividually(): void {
    console.log('Loading metrics individually as fallback...');

    // Load total employees
    this.dashboardMetricsService
      .getTotalEmployeesForUser(this.userId, this.currentSelectedCompanyId)
      .subscribe({
        next: (count) => {
          this.totalEmployees = count;
        },
        error: (err) => {
          console.error('Error loading total employees:', err);
          this.totalEmployees = 0;
        },
      });

    // Load total companies
    this.dashboardMetricsService
      .getTotalCompaniesForUser(this.userId)
      .subscribe({
        next: (count) => {
          this.companyCount = count;
        },
        error: (err) => {
          console.error('Error loading total companies:', err);
          this.companyCount = 0;
        },
      });

    // Load total active policies
    this.dashboardMetricsService
      .getTotalActivePoliciesForUser(this.userId, this.currentSelectedCompanyId)
      .subscribe({
        next: (count) => {
          this.totalActivePolicies = count;
        },
        error: (err) => {
          console.error('Error loading total active policies:', err);
          this.totalActivePolicies = 0;
        },
      });

    // Load active quotes
    this.dashboardMetricsService
      .getActiveQuotesForUser(this.userId, this.currentSelectedCompanyId)
      .subscribe({
        next: (count) => {
          this.activeQuotes = count;
        },
        error: (err) => {
          console.error('Error loading active quotes:', err);
          this.activeQuotes = 0;
        },
      });
  }

  /**
   * Refresh dashboard data
   */
  refreshDashboard(): void {
    if (this.userId) {
      this.loadDashboardMetrics();
    }
  }

  /**
   * load companies for user
   */
  fetchCompanies(): void {
    if (!this.userId) {
      return;
    }

    this.isLoading = true;
    this.companyService.getCompaniesByUserId(this.userId).subscribe({
      next: (data) => {
        this.companies = data;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error fetching companies:', err);
        this.toastr.error('Failed to load companies', 'Error');
      },
    });
  }

  /**
   * load dashboard data based on company
   */
  onCompanyChange(): void {
    this.companyService.setSelectedCompany(this.currentSelectedCompanyId);
    this.loadDashboardMetrics();
  }

  getCompanyName(companyId: number): string {
    const company = this.companies.find((c) => c.id == companyId);
    return company ? company.name : '';
  }
}
